import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { UiIcon } from 'ui-components';
import { getCurrentSlice } from 'utils';
import { applyFiltersToApiRequest, toggleSidebarFilters } from 'store';
import { useRouter } from 'hooks';
import { StyledSelectedFilters } from './SelectedFilters.styles';
import SelectedFilter from './SelectedFilter';

const visibleFiltersCount = 5;

let sliceModule = {};

/**
 * URL params useQueryStringFilters.js re-applies as a filter selection on
 * every render. Removing a filter only clears its own param(s) here, so
 * deleting e.g. sentimentFilter can't also drop resource_id/resource_name and
 * silently bump a resource-scoped contents page back to the plain one.
 * resourcesFilter is the one exception where dropping resource_id/resource_name
 * is the intended outcome, since that scope no longer applies once removed.
 */
const filterComponentNameToRouterParams = {
  resourcesFilter: ['resource_id', 'resource_name'],
  hashtagCloudFilter: ['hashtag_phrase'],
  wordCloudFilter: ['word_phrase'],
  contentTagFilter: ['content_tag'],
  resourceCategoryFilter: ['resource_category'],
  sentimentFilter: ['sentiment'],
};

export default function SelectedFilters() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const filters = useSelector(
    (state) => state[state.filterReducer.filtersContext]?.filters
  );
  const reducerName = useSelector(
    (state) => state.filterReducer.filtersContext
  );
  const { clearRouterParams } = useRouter();

  useEffect(() => {
    if (reducerName) {
      sliceModule = getCurrentSlice(reducerName);
    }
  }, [reducerName]);

  const handleDelete = (componentName) => {
    dispatch(sliceModule?.resetFilter({ componentName }));
    const routerParams = filterComponentNameToRouterParams?.[componentName];
    if (routerParams) {
      clearRouterParams(routerParams);
    }
    dispatch(applyFiltersToApiRequest());
  };

  const allSelectedFilters = Object.keys(filters || []).filter(
    (key) => filters[key].hasFilter
  );

  const otherFiltersCount = allSelectedFilters.length - visibleFiltersCount;

  const handleOpen = (componentName) => {
    dispatch(
      sliceModule?.setSelectedFilter(
        componentName || allSelectedFilters[visibleFiltersCount]
      )
    );
    dispatch(toggleSidebarFilters(true));
  };

  return (
    <StyledSelectedFilters>
      {allSelectedFilters.slice(0, visibleFiltersCount).map((key) => {
        const { title, translateKey, componentName } = filters[key];
        return (
          <SelectedFilter
            label={t(title || translateKey)}
            onDelete={() => handleDelete(componentName)}
            onClick={() => handleOpen(componentName)}
            deleteIcon={<UiIcon icon='ri-close-line' />}
          />
        );
      })}

      {otherFiltersCount > 0 && (
        <SelectedFilter
          label={t('content:otherFilters', {
            count: otherFiltersCount,
          })}
          onClick={() => handleOpen()}
        />
      )}
    </StyledSelectedFilters>
  );
}
